import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RiskAssessmentDashboard = () => {
  const [selectedRisk, setSelectedRisk] = useState(null);

  const riskAssessments = [
    {
      id: 1,
      title: "PF Contribution Delay",
      category: "Provident Fund",
      severity: "high",
      riskScore: 85,
      description: "Delayed PF contribution for 3 employees in December 2024",
      impact: "Potential penalty of ₹25,000 and interest charges",
      recommendedActions: [
        "Immediate payment of pending contributions",
        "Update employee records in PF portal",
        "Set up automated payment reminders"
      ],
      dueDate: "2025-01-25",
      assignedTo: "HR Manager",
      status: "open"
    },
    {
      id: 2,
      title: "TDS Calculation Discrepancy",
      category: "Tax Deduction",
      severity: "medium",
      riskScore: 65,
      description: "Mismatch in TDS calculations for 5 employees in Q3 2024",
      impact: "Potential scrutiny from tax authorities and revision requirements",
      recommendedActions: [
        "Review salary structures and exemptions",
        "Recalculate TDS for affected employees",
        "File revised returns if necessary"
      ],
      dueDate: "2025-01-31",
      assignedTo: "Finance Team",
      status: "in-progress"
    },
    {
      id: 3,
      title: "ESI Registration Pending",
      category: "Employee Insurance",
      severity: "high",
      riskScore: 90,
      description: "New employees not registered in ESI portal within statutory timeline",
      impact: "Non-compliance penalty and coverage issues for employees",
      recommendedActions: [
        "Complete ESI registration for new joiners",
        "Update employee database",
        "Implement automated registration process"
      ],
      dueDate: "2025-01-22",
      assignedTo: "Compliance Officer",
      status: "open"
    },
    {
      id: 4,
      title: "Professional Tax Arrears",
      category: "State Tax",
      severity: "low",
      riskScore: 35,
      description: "Minor arrears in professional tax for Karnataka state",
      impact: "Small penalty and interest charges",
      recommendedActions: [
        "Clear pending arrears",
        "Verify state-wise tax calculations",
        "Update payment schedules"
      ],
      dueDate: "2025-02-15",
      assignedTo: "Payroll Admin",
      status: "resolved"
    }
  ];

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high':
        return 'text-error bg-error/10 border-error/20';
      case 'medium':
        return 'text-warning bg-warning/10 border-warning/20';
      case 'low':
        return 'text-success bg-success/10 border-success/20';
      default:
        return 'text-muted-foreground bg-muted border-border';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open':
        return 'text-error bg-error/10 border-error/20';
      case 'in-progress':
        return 'text-warning bg-warning/10 border-warning/20';
      case 'resolved':
        return 'text-success bg-success/10 border-success/20';
      default:
        return 'text-muted-foreground bg-muted border-border';
    }
  };

  const getRiskScoreColor = (score) => {
    if (score >= 80) return 'text-error';
    if (score >= 60) return 'text-warning';
    return 'text-success';
  };

  const handleViewDetails = (risk) => {
    setSelectedRisk(risk);
  };

  const handleCloseDetails = () => {
    setSelectedRisk(null);
  };

  const handleAssignRisk = (riskId) => {
    console.log(`Assigning risk ${riskId} to team member`);
  };

  const handleMarkResolved = (riskId) => {
    console.log(`Marking risk ${riskId} as resolved`);
  };

  const riskSummary = {
    total: riskAssessments.length,
    high: riskAssessments.filter(r => r.severity === 'high').length,
    medium: riskAssessments.filter(r => r.severity === 'medium').length,
    low: riskAssessments.filter(r => r.severity === 'low').length,
    open: riskAssessments.filter(r => r.status === 'open').length
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="AlertTriangle" size={20} className="text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Risk Assessment Dashboard</h3>
            <p className="text-sm text-muted-foreground">Monitor compliance risks and recommended actions</p>
          </div>
        </div>
        <Button variant="outline" iconName="Plus" iconPosition="left">
          Add Risk
        </Button>
      </div>

      {/* Risk Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <div className="bg-muted/30 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-foreground">{riskSummary.total}</div>
          <div className="text-sm text-muted-foreground">Total Risks</div>
        </div>
        <div className="bg-error/5 border border-error/20 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-error">{riskSummary.high}</div>
          <div className="text-sm text-muted-foreground">High Risk</div>
        </div>
        <div className="bg-warning/5 border border-warning/20 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-warning">{riskSummary.medium}</div>
          <div className="text-sm text-muted-foreground">Medium Risk</div>
        </div>
        <div className="bg-success/5 border border-success/20 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-success">{riskSummary.low}</div>
          <div className="text-sm text-muted-foreground">Low Risk</div>
        </div>
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-primary">{riskSummary.open}</div>
          <div className="text-sm text-muted-foreground">Open Items</div>
        </div>
      </div>

      {/* Risk List */}
      <div className="space-y-4">
        {riskAssessments.map((risk) => (
          <div key={risk.id} className="border border-border rounded-lg p-4 hover:shadow-sm transition-shadow">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h4 className="font-semibold text-foreground">{risk.title}</h4>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getSeverityColor(risk.severity)}`}>
                    {risk.severity.toUpperCase()}
                  </span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(risk.status)}`}>
                    {risk.status.replace('-', ' ').toUpperCase()}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mb-2">{risk.description}</p>
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-muted-foreground">Category: <span className="text-foreground">{risk.category}</span></span>
                  <span className="text-muted-foreground">Due: <span className="text-foreground">{new Date(risk.dueDate).toLocaleDateString('en-IN')}</span></span>
                  <span className="text-muted-foreground">Assigned: <span className="text-foreground">{risk.assignedTo}</span></span>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <div className={`text-2xl font-bold ${getRiskScoreColor(risk.riskScore)}`}>
                    {risk.riskScore}
                  </div>
                  <div className="text-xs text-muted-foreground">Risk Score</div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Eye"
                    onClick={() => handleViewDetails(risk)}
                  >
                    Details
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="UserPlus"
                    onClick={() => handleAssignRisk(risk.id)}
                  >
                    Assign
                  </Button>
                  {risk.status !== 'resolved' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="CheckCircle"
                      onClick={() => handleMarkResolved(risk.id)}
                      className="text-success hover:text-success hover:bg-success/10"
                    >
                      Resolve
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Risk Details Modal */}
      {selectedRisk && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-foreground">{selectedRisk.title}</h3>
                <Button variant="ghost" size="icon" onClick={handleCloseDetails}>
                  <Icon name="X" size={20} />
                </Button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              <div>
                <h4 className="font-medium text-foreground mb-2">Risk Details</h4>
                <p className="text-sm text-muted-foreground">{selectedRisk.description}</p>
              </div>
              
              <div>
                <h4 className="font-medium text-foreground mb-2">Impact Assessment</h4>
                <p className="text-sm text-muted-foreground">{selectedRisk.impact}</p>
              </div>
              
              <div>
                <h4 className="font-medium text-foreground mb-2">Recommended Actions</h4>
                <ul className="space-y-2">
                  {selectedRisk.recommendedActions.map((action, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm">
                      <Icon name="ArrowRight" size={16} className="text-primary mt-0.5 flex-shrink-0" />
                      <span className="text-muted-foreground">{action}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border">
                <div>
                  <span className="text-sm text-muted-foreground">Risk Score</span>
                  <div className={`text-xl font-bold ${getRiskScoreColor(selectedRisk.riskScore)}`}>
                    {selectedRisk.riskScore}/100
                  </div>
                </div>
                <div>
                  <span className="text-sm text-muted-foreground">Due Date</span>
                  <div className="text-sm font-medium text-foreground">
                    {new Date(selectedRisk.dueDate).toLocaleDateString('en-IN')}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RiskAssessmentDashboard;